


import React, { useState, useEffect, useMemo } from 'react';
import { DashboardHeader } from './DashboardHeader';
import { DataContractCard } from './DataContractCard';
import { DataContractDetailsModal } from './DataContractDetailsModal';
import axios from 'axios';
import heartLoading from "../../../assests/heartLoading.gif";
import { RotateCcw, Filter } from "lucide-react";
import InfoIcon from '@mui/icons-material/Info';
import { Tooltip } from '@mui/material';
import { replace, useLocation, useNavigate } from 'react-router-dom';
import AppliedFilters from "../../common/AppliedFilters";
import ReusableFilterDrawer from "../../common/ReusableFilterDrawer";
import CustomMultiSelect from "../DataContract/Viewkpi/CustomMultiSelect";



function LandingPage() {
   // Data states
  const [dataContracts, setDataContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Applied filters (used for API calls)
  const [appliedParentDomain, setAppliedParentDomain] = useState([]);
  const [appliedDomain, setAppliedDomain] = useState([]);
  const [appliedSubDomain, setAppliedSubDomain] = useState([]);
  const [appliedProduct, setAppliedProduct] = useState([]);
  
  // Hierarchical filters from API
  const [hierarchicalFilters, setHierarchicalFilters] = useState(null);
  const [filterOptions, setFilterOptions] = useState({
    parentDomains: [],
    domains: [],
    subDomains: [],
    products: [],
  });
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Modal states
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false)
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const viewProduct = params.get("viewProduct");
  // Fetch traffic light data from API
  const fetchTrafficLightData = async (payload = null) => {
    try {
      const requestPayload = payload || {
        parentDomain: (appliedParentDomain || []).join(","),
        domain: (appliedDomain || []).join(","),
        subDomain: (appliedSubDomain || []).join(","),
        product: viewProduct ? viewProduct : (appliedProduct || []).join(","),
        searchValue: "",
        page: currentPage,
        pageSize: pageSize,
      };

      const apiEndpoint = `${process.env.REACT_APP_API_URL}/get-trafficlight-results`;

      const response = await axios.get(apiEndpoint, {params: requestPayload});

      if (response.data) {
        setDataContracts(response.data.dataContracts || []);
        setTotalCount(response.data.totalCount || 0);
        setTotalPages(response.data.totalPages || 0);
        setCurrentPage(response.data.page || 1);
        setPageSize(response.data.pageSize || 10);

        // Handle filter options from API response
        if (response.data.filters && response.data.filters.parent_domains) {
          setHierarchicalFilters(response.data.filters);

          // Extract ALL available options for each filter level
          const parentDomains = response.data.filters.parent_domains.map(
            (pd) => pd.parent_domain
          );

          // Get ALL domains
          const allDomains = [];
          response.data.filters.parent_domains.forEach((pd) => {
            pd.domains.forEach((d) => {
              if (!allDomains.includes(d.domain)) {
                allDomains.push(d.domain);
              }
            });
          });

          // Get ALL sub domains
          const allSubDomains = [];
          response.data.filters.parent_domains.forEach((pd) => {
            pd.domains.forEach((d) => {
              d.sub_domains.forEach((sd) => {
                if (!allSubDomains.includes(sd.sub_domain)) {
                  allSubDomains.push(sd.sub_domain);
                }
              });
            });
          });

          // Get ALL products
          const allProducts = [];
          response.data.filters.parent_domains.forEach((pd) => {
            pd.domains.forEach((d) => {
              d.sub_domains.forEach((sd) => {
                sd.products.forEach((p) => {
                  if (!allProducts.includes(p)) {
                    allProducts.push(p);
                  }
                });
              });
            });
          });

          setFilterOptions({
            parentDomains: parentDomains,
            domains: allDomains,
            subDomains: allSubDomains,
            products: allProducts,
          });
        }

        if (viewProduct && response.data?.dataContracts?.length) {
          handleViewDetails(response.data.dataContracts[0])
        }
      }
    } catch (error) {
      console.error("Error fetching traffic light data:", error);
      setDataContracts([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
      setFilterLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    setLoading(true);
    fetchTrafficLightData();
  }, [viewProduct]);

  // Show all contracts without status filtering (status filter is commented out)
  const filteredContracts = useMemo(() => {
    return dataContracts;
  }, [dataContracts]);

  const stats = useMemo(() => ({
    total: dataContracts.length,
    healthy: dataContracts.filter(c => c.status === 'healthy').length,
    warning: dataContracts.filter(c => c.status === 'warning').length,
    critical: dataContracts.filter(c => c.status === 'critical').length
  }), [dataContracts]);

  const handleDrawerApply = (drawerFilters) => {
    const pd = drawerFilters.parentDomain || [];
    const d = drawerFilters.domain || [];
    const sd = drawerFilters.subDomain || [];
    const p = drawerFilters.product || [];
    setAppliedParentDomain(pd);
    setAppliedDomain(d);
    setAppliedSubDomain(sd);
    setAppliedProduct(p);
    setCurrentPage(1);
    setFilterLoading(true);
    fetchTrafficLightData({
      parentDomain: pd.join(","),
      domain: d.join(","),
      subDomain: sd.join(","),
      product: p.join(","),
      searchValue: "",
      page: 1,
      pageSize,
    });
  };

  const filterSections = useMemo(() => {
    const pdList = (hierarchicalFilters?.parent_domains || []);
    return [
      {
        fields: [
          {
            type: "custom",
            key: "parentDomain",
            defaultValue: [],
            render: (localFilters, setLocalFilters) => {
              const opts = pdList.map((pd) => ({ value: pd.parent_domain, label: pd.parent_domain }));
              const currentValue = opts.filter((o) => (localFilters.parentDomain || []).includes(o.value));
              return (
                <CustomMultiSelect
                  label="Parent Domain"
                  options={opts}
                  value={currentValue}
                  placeholder="All Parent Domains"
                  onChange={(_, v) => {
                    const vals = (v || []).map((o) => o.value);
                    setLocalFilters((prev) => ({ ...prev, parentDomain: vals, domain: [], subDomain: [], product: [] }));
                  }}
                />
              );
            },
          },
          {
            type: "custom",
            key: "domain",
            defaultValue: [],
            render: (localFilters, setLocalFilters) => {
              const pdData = localFilters.parentDomain?.length ? pdList.filter((p) => localFilters.parentDomain.includes(p.parent_domain)) : pdList;
              const seen = new Set();
              const opts = [];
              pdData.forEach((pd) => (pd.domains || []).forEach((d) => {
                if (!seen.has(d.domain)) { seen.add(d.domain); opts.push({ value: d.domain, label: d.domain }); }
              }));
              const currentValue = opts.filter((o) => (localFilters.domain || []).includes(o.value));
              return (
                <CustomMultiSelect
                  label="Domain"
                  options={opts}
                  value={currentValue}
                  placeholder="All Domains"
                  onChange={(_, v) => {
                    const vals = (v || []).map((o) => o.value);
                    setLocalFilters((prev) => ({ ...prev, domain: vals, subDomain: [], product: [] }));
                  }}
                />
              );
            },
          },
          {
            type: "custom",
            key: "subDomain",
            defaultValue: [],
            render: (localFilters, setLocalFilters) => {
              const pdData = localFilters.parentDomain?.length ? pdList.filter((p) => localFilters.parentDomain.includes(p.parent_domain)) : pdList;
              const domainList = pdData.flatMap((pd) => pd.domains || []);
              const filteredDomains = localFilters.domain?.length ? domainList.filter((d) => localFilters.domain.includes(d.domain)) : domainList;
              const seen = new Set();
              const opts = [];
              filteredDomains.forEach((d) => (d.sub_domains || []).forEach((sd) => {
                if (!seen.has(sd.sub_domain)) { seen.add(sd.sub_domain); opts.push({ value: sd.sub_domain, label: sd.sub_domain }); }
              }));
              const currentValue = opts.filter((o) => (localFilters.subDomain || []).includes(o.value));
              return (
                <CustomMultiSelect
                  label="Sub Domain"
                  options={opts}
                  value={currentValue}
                  placeholder="All Sub Domains"
                  onChange={(_, v) => {
                    const vals = (v || []).map((o) => o.value);
                    setLocalFilters((prev) => ({ ...prev, subDomain: vals, product: [] }));
                  }}
                />
              );
            },
          },
          {
            type: "custom",
            key: "product",
            defaultValue: [],
            render: (localFilters, setLocalFilters) => {
              const pdData = localFilters.parentDomain?.length ? pdList.filter((p) => localFilters.parentDomain.includes(p.parent_domain)) : pdList;
              const domainList = pdData.flatMap((pd) => pd.domains || []);
              const filteredDomains = localFilters.domain?.length ? domainList.filter((d) => localFilters.domain.includes(d.domain)) : domainList;
              const sdList = filteredDomains.flatMap((d) => d.sub_domains || []);
              const filteredSds = localFilters.subDomain?.length ? sdList.filter((s) => localFilters.subDomain.includes(s.sub_domain)) : sdList;
              const seen = new Set();
              const opts = [];
              filteredSds.forEach((sd) => (sd.products || []).forEach((p) => {
                if (!seen.has(p)) { seen.add(p); opts.push({ value: p, label: p }); }
              }));
              const currentValue = opts.filter((o) => (localFilters.product || []).includes(o.value));
              return (
                <CustomMultiSelect
                  label="Product"
                  options={opts}
                  value={currentValue}
                  placeholder="All Products"
                  onChange={(_, v) => {
                    const vals = (v || []).map((o) => o.value);
                    setLocalFilters((prev) => ({ ...prev, product: vals }));
                  }}
                />
              );
            },
          },
        ],
      },
    ];
  }, [hierarchicalFilters]);

  const handleResetFilters = () => {
    setAppliedParentDomain([]);
    setAppliedDomain([]);
    setAppliedSubDomain([]);
    setAppliedProduct([]);
    setCurrentPage(1);
    setFilterLoading(true);
    fetchTrafficLightData({ parentDomain: "", domain: "", subDomain: "", product: "", searchValue: "", page: 1, pageSize });
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchTrafficLightData();
    setIsRefreshing(false);
  };

  const handleViewDetails = (contract) => {
    setSelectedContract(contract);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedContract(null);
    if (viewProduct){
      navigate("/data-traffic-lights", { replace: true });
    }
  };

  const handlePageChange = async (newPage) => {
    setCurrentPage(newPage);

    const payload = {
      parentDomain: (appliedParentDomain || []).join(","),
      domain: (appliedDomain || []).join(","),
      subDomain: (appliedSubDomain || []).join(","),
      product: (appliedProduct || []).join(","),
      searchValue: "",
      page: newPage,
      pageSize: pageSize,
    };

    await fetchTrafficLightData(payload);
  };

  if (loading) {
    return (
      <div className="relative bg-white flex items-center justify-center z-50 min-h-screen">
        <div className="flex flex-col items-center space-y-3">
          <img src={heartLoading} alt="Loading..." style={{ width : 200}} />
          <span className="text-gray-700 text-[20px] text-center">
            Loading traffic light data...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-[#f9fafb] px-6 py-2'>
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center items-center lg:justify-between pb-3">
        <div>
          <h1 className="font-bold pt-4 font-family-poppins text-[24px] text-[#424242]">Data Traffic Light</h1>
          <p className="pb-2">Real-time monitoring of data contract health and compliance</p>
        </div>
        
        <div className="flex items-center gap-4 mt-1">
          <div className="flex items-center gap-4 bg-white rounded-lg p-3 shadow-sm border">
            <div className="flex items-center gap-2">
              <Tooltip title="All underlying assets have passed their data quality rules and in turn the dimensions, in the latest pipeline run." placement='top' arrow ><InfoIcon sx={{color: "green"}} /></Tooltip>
              <span className="text-sm font-medium text-gray-700">{stats.healthy} Healthy</span>
            </div>
            <div className="flex items-center gap-2">
            <Tooltip title="One or more rules of data asset(s) within the product have failed, but dimensions(s) of each asset(s) have passed.  " placement='top' arrow ><InfoIcon sx={{color: "#FFBF00"}} /></Tooltip>

              <span className="text-sm font-medium text-gray-700">{stats.warning} Warning</span>
            </div>
            <div className="flex items-center gap-2">
            <Tooltip title=" One or more rules of data assets within the product have failed, and dimension(s) of asset(s) have also failed." placement='top' arrow ><InfoIcon sx={{color: "red"}} /></Tooltip>

              <span className="text-sm font-medium text-gray-700">{stats.critical} Critical</span>
            </div>
          </div>
        </div>
      </div>

     

      {/* Filters Section */}
      <div className="bg-white p-2 mb-4">
        <div className="flex justify-end items-center gap-2">
          <button
            onClick={() => setFilterDrawerOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border transition-colors h-[45px] whitespace-nowrap bg-[#bb1ccc] text-white border-[#bb1ccc]"
          >
            <Filter className="h-4 w-4" />
            Filters
            {[...(appliedParentDomain || []), ...(appliedDomain || []), ...(appliedSubDomain || []), ...(appliedProduct || [])].length > 0 && (
              <span className="bg-white text-[#bb1ccc] text-xs rounded-full px-1.5 py-0.5 leading-none font-semibold">
                {[...(appliedParentDomain || []), ...(appliedDomain || []), ...(appliedSubDomain || []), ...(appliedProduct || [])].length}
              </span>
            )}
          </button>
          <button
            onClick={handleResetFilters}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 h-[45px] whitespace-nowrap transition-colors"
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </button>
        </div>
      </div>

      <ReusableFilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        sections={filterSections}
        values={{
          parentDomain: appliedParentDomain || [],
          domain: appliedDomain || [],
          subDomain: appliedSubDomain || [],
          product: appliedProduct || [],
        }}
        onApply={handleDrawerApply}
        onReset={handleResetFilters}
        flat
        subtitle="Filter by domain hierarchy"
      />

     
      {(() => {
        const chips = [
          ...(appliedParentDomain || []).map((v) => ({ label: "Parent Domain", value: v })),
          ...(appliedDomain || []).map((v) => ({ label: "Domain", value: v })),
          ...(appliedSubDomain || []).map((v) => ({ label: "Sub Domain", value: v })),
          ...(appliedProduct || []).map((v) => ({ label: "Product", value: v })),
        ];
        return chips.length > 0 ? (
          <div className="px-2 pb-2">
            <AppliedFilters chips={chips} />
          </div>
        ) : null;
      })()}

      {filterLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <img src={heartLoading} alt="Loading..." style={{ width : 200}} />
            <p className="text-gray-600">Loading contracts...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Contract Cards */}
          <div className='px-4' >
          <div className="grid [grid-template-columns:repeat(auto-fit,minmax(296px,296px))] gap-4 justify-start">
            {filteredContracts.map((contract) => (
              <DataContractCard 
                key={contract.id} 
                {...contract} 
                onViewDetails={() => handleViewDetails(contract)}
              />
            ))}
          </div>
          </div>

          {/* No Results State */}
          {filteredContracts.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-gray-400">📊</span>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No contracts found</h3>
              <p className="text-gray-600">
                {(appliedParentDomain || []).length > 0 || (appliedDomain || []).length > 0 || (appliedSubDomain || []).length > 0 || (appliedProduct || []).length > 0
                  ? 'Try adjusting your filter criteria'
                  : 'No data contracts are currently configured'
                }
              </p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 px-4">
              <span className="text-sm text-gray-600">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
              </span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <div className="flex items-center space-x-1">
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={i}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-1 text-sm font-medium rounded-md ${
                          currentPage === pageNum
                            ? 'bg-[#0171C1] text-white'
                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Contract Details Modal */}
      <DataContractDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        contract={selectedContract}
      />
    </div>
  );
}

export default LandingPage;
